import type { Capabilities, TableFetchParams } from '../../ports/table-adapter';
import { BaseTableAdapter } from '../../domain/query/base-adapter';
import { AUTO, type FieldType } from '../../domain/query/field';
import type { PageRequest, PageResult } from '../../domain/query/paging';
import {
    applyFilters,
    applySearch,
    applySort,
} from '../../domain/query/predicates';

export interface StaticOpts {
    pageSize?: number;
    searchFieldPaths?: string[];
    fields?: Record<string, FieldType>;
}

interface Slice<T> {
    slice: T[];
    offset: number;
    hasNext: boolean;
    hasPrev: boolean;
    nextToken?: number;
    prevToken?: number;
}

function sliceForPage<T>(
    rows: T[],
    size: number | undefined,
    page: PageRequest,
): Slice<T> {
    if (!size)
        return { slice: rows, offset: 0, hasNext: false, hasPrev: false };

    let start = 0;
    switch (page.kind) {
        case 'at':
            start = page.index * size;
            break;
        case 'after':
        case 'before':
            start = page.token as number;
            break;
        case 'last':
            start = Math.max(0, rows.length - size);
            break;
        default:
            start = 0;
    }

    const hasNext = start + size < rows.length;
    const hasPrev = start > 0;
    return {
        slice: rows.slice(start, start + size),
        offset: start,
        hasNext,
        hasPrev,
        nextToken: hasNext ? start + size : undefined,
        prevToken: hasPrev ? Math.max(0, start - size) : undefined,
    };
}

export class StaticTableAdapter<T> extends BaseTableAdapter<T> {
    #rows: T[];
    #searchFieldPaths?: string[];
    #fields?: Record<string, FieldType>;

    constructor(rows: T[], opts?: StaticOpts) {
        super(opts?.pageSize);
        this.#rows = rows;
        this.#searchFieldPaths = opts?.searchFieldPaths;
        this.#fields = opts?.fields;
    }

    protected caps(): Capabilities {
        return { totalCount: true, sort: true, filter: true };
    }

    protected fieldFor(key: string): FieldType {
        const field = this.#fields?.[key];
        if (!field) {
            throw new Error(
                `StaticTableAdapter: no field type registered for sort key "${key}". Add it to the "fields" option.`,
            );
        }
        return field;
    }

    protected moveNext(): void {
        this.moveAt(this.currentPage + 1);
    }
    protected movePrev(): void {
        this.moveAt(this.currentPage - 1);
    }

    next(): void {
        this.moveNext();
    }
    prev(): void {
        this.movePrev();
    }
    first(): void {
        this.moveFirst();
    }
    last(): void {
        this.moveLast();
    }
    goTo(index: number): void {
        this.moveAt(index);
    }

    #computeResult(
        params: Omit<TableFetchParams, 'signal' | 'force'>,
    ): PageResult<T> {
        const readValue = (row: T, key: string): unknown => this.read(row, key);
        let rows = applyFilters(this.#rows, params.filters, readValue);
        const keys = this.#searchFieldPaths ?? params.filters.map((f) => f.key);
        rows = applySearch(rows, params.search, keys, readValue);
        const comparator = params.sort
            ? this.fieldFor(params.sort.key).sort
            : AUTO.sort;
        rows = applySort(rows, params.sort, readValue, comparator);

        const { slice, offset, hasNext, hasPrev, nextToken, prevToken } =
            sliceForPage(rows, params.pageSize, params.page);
        return {
            rows: slice,
            totalCount: rows.length,
            offset,
            hasNext,
            hasPrev,
            nextToken,
            prevToken,
        };
    }

    protected fetchPage(params: TableFetchParams): Promise<PageResult<T>> {
        return Promise.resolve(this.#computeResult(params));
    }

    setRows(rows: T[]): void {
        this.#rows = rows;
        const req = this.currentRequest;
        let result: PageResult<T>;
        try {
            result = this.#computeResult(this.buildFetchParams(req));
        } catch (err) {
            this.reportError(err);
            return;
        }
        if (
            result.rows.length === 0 &&
            result.hasPrev &&
            this.currentPage > 0
        ) {
            // The current page is now empty (e.g. its last row was
            // removed) - fall back to the adapter's normal async refresh,
            // which already knows how to step back a page. This is the
            // only case setRows doesn't resolve synchronously.
            this.refresh();
            return;
        }
        this.commitPageResult(result, req);
    }
}
